// Product API functions
import { getUserInfo } from './auth';
import { CommonUtils, parseApiCountMap } from './commonUtils';
import { getApiV1Url } from '../config/api';

// ============================================================================
// INTERFACES
// ============================================================================

// 기본 상품 정보
export interface Product {
  projectCode: string;
  brandCode?: string;
  code: string;
  itemCode: string;
  status: string;
  name: string;
  shortName?: string;
  posCode?: string;
  originalPrice: number;
  salePrice: number;
  comment?: string;
  rewardStampCount: number;
  rewardPointRatio: number;
  discount: number;
  voucher: number;
  reservation: number;
  delivery: number;
  shipping: number;
  description?: string;
  /** 대표 파일 버전 (CDN `?v=` 우선 사용, API Long / DB bigint) */
  primaryVersion?: number | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  mappingOptionGroupCount?: number;
  badges?: Array<{
    title: string;
  }>;
}

// 상품 상세 정보 (Product와 동일)
export interface ProductDetail extends Product {}

/** 상품 상세 API 응답 전용 필드 (폼/업데이트 대상 아님) */
export interface ProductDetailResponse extends ProductDetail {
  /** 상품이 속한 트리 코드 목록 (그룹코드:노드코드) */
  treeCodes?: string[];
  /** 트리별 정렬 정보 (treeCode -> fractional index) */
  treeSortInfo?: Record<string, string>;
  categoryCode?: string;
  brandName?: string;
}

// 상품 생성 요청
export interface ProductCreateRequest {
  brandCode?: string;
  itemCode: string;
  status: string;
  name: string;
  shortName?: string;
  posCode?: string;
  originalPrice: number;
  salePrice: number;
  comment?: string;
  rewardStampCount: number;
  rewardPointRatio: number;
  discount: number;
  voucher: number;
  reservation: number;
  delivery: number;
  shipping: number;
  description?: string;
}

// 상품 수정 요청
export interface ProductUpdateRequest {
  brandCode?: string;
  itemCode: string;
  status: string;
  name: string;
  shortName?: string;
  posCode?: string;
  originalPrice: number;
  salePrice: number;
  comment?: string;
  rewardStampCount: number;
  rewardPointRatio: number;
  discount: number;
  voucher: number;
  reservation: number;
  delivery: number;
  shipping: number;
  description?: string;
}

// 상품 목록 요청
export interface ProductListRequest {
  page?: number;
  size?: number;
  sort?: string;
  keyword?: string;
  status?: string;
  brandCode?: string;
  view?: string;
}

// 상품 목록 응답
export interface ProductListResponse {
  totalCount: number;
  products: Product[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// 상품 생성 응답
export interface ProductCreateResponse {
  success: boolean;
  message: string;
  data?: {
    code: string;
    itemCode: string;
    name: string;
  };
}

// 상품 수정 응답
export interface ProductUpdateResponse {
  success: boolean;
  message: string;
  data?: {
    code: string;
    itemCode: string;
    name: string;
  };
}

// 상품 상태별 건수 응답
export interface ProductStatusCount {
  count: number;
  ACTIVE: number;
  INACTIVE: number;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

// 상품 생성 API
export const createProduct = async (
  projectCode: string, 
  productData: ProductCreateRequest
): Promise<ProductCreateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/products`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(productData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return {
      success: true,
      message: '상품이 성공적으로 생성되었습니다.',
      data: result
    };
  } catch (error: any) {
    console.error('상품 생성 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 상품 목록 조회 API
export const getProductList = async (
  projectCode: string,
  params: ProductListRequest = {}
): Promise<ProductListResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.keyword) queryParams.append('keyword', params.keyword);
    if (params.status) queryParams.append('status', params.status);
    if (params.brandCode) queryParams.append('brandCode', params.brandCode);
    if (params.view) queryParams.append('view', params.view);
    
    const response = await fetch(
      `${getApiV1Url(`/projects/${projectCode}/products`)}?${queryParams}`,
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('상품 목록 서버 응답:', result);
    console.log('서버 응답 data 필드:', result.data);
    
    if (result.data) {
      return {
        totalCount: result.data.totalCount,
        products: result.data.products || [],
        totalPages: result.data.totalPages,
        currentPage: result.data.currentPage,
        pageSize: result.data.pageSize,
        hasNext: result.data.hasNext,
        hasPrevious: result.data.hasPrevious
      };
    }
    
    return result;
  } catch (error: any) {
    console.error('상품목록 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 상품 상세 조회 API
export const getProductDetail = async (
  projectCode: string,
  productCode: string
): Promise<ProductDetailResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/products/${productCode}`),
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('상품 상세 API 응답:', result);
    
    return result.data;
  } catch (error: any) {
    console.error('상품 상세 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 상품 수정 API
export const updateProduct = async (
  projectCode: string,
  productCode: string,
  productData: ProductUpdateRequest
): Promise<ProductUpdateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/products/${productCode}`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(productData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return {
      success: true,
      message: '상품이 성공적으로 수정되었습니다.',
      data: result
    };
  } catch (error: any) {
    console.error('상품 수정 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 상품 삭제 API
export const deleteProduct = async (
  projectCode: string, 
  productCode: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/products/${productCode}`),
      {
        method: 'DELETE',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      message: '상품이 성공적으로 삭제되었습니다.'
    };
  } catch (error: any) {
    console.error('상품 삭제 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 상품 상태별 건수 조회 API
// GET /api/v1/projects/{projectCode}/product-stats/count/status
export const getProductStatusCount = async (
  projectCode: string
): Promise<ProductStatusCount> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/product-stats/count/status`),
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    const data = (result?.data ?? result ?? {}) as Record<string, unknown>;
    const counts = parseApiCountMap(data.counts ?? data);

    const active = counts.ACTIVE ?? Number(data.ACTIVE ?? 0);
    const inactive = counts.INACTIVE ?? Number(data.INACTIVE ?? 0);
    const count =
      Object.keys(counts).length > 0
        ? Object.values(counts).reduce((sum, v) => sum + v, 0)
        : Number(data.count ?? active + inactive);

    return {
      count: Number.isFinite(count) ? count : 0,
      ACTIVE: Number.isFinite(active) ? active : 0,
      INACTIVE: Number.isFinite(inactive) ? inactive : 0,
    };
  } catch (error: any) {
    console.error('상품 상태별 건수 조회 중 오류 발생:', error);

    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }

    throw error;
  }
};